import { createContext, useContext, useEffect, useState, useCallback } from "react";
import LS_API from "../api/axios";
import { getAccessToken } from "../api/axios";
import {ADMIN_PERMISSION_EDIT} from "../component/permission/permission-consts";
import logger from "../utils/logger";

export const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
    const [permissions, setPermissions] = useState([]);
    const[roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [provisioning, setProvisioning] = useState(false);
    const [hasCheckedOnce, setHasCheckedOnce] = useState(false);

    const [userProfile, setUserProfile] = useState(null);

    // ✅ NEW: tracks whether the backend is unreachable / returning 5xx
    const [isAppDown, setIsAppDown] = useState(false);

    // Track sync attempts to prevent infinite retries
    const [syncAttempts, setSyncAttempts] = useState(0);
    const MAX_SYNC_ATTEMPTS = 2;

    const fetchUserProfile = useCallback(async () => {
        const token = getAccessToken();
        if (!token) {
            logger.warn("No access token found, skipping user profile fetch");
            setLoading(false);
            setHasCheckedOnce(true);
            return;
        }

        setLoading(true);
        try {
            const res = await LS_API.get("/api/users/me");

            // If we reach here the server is up — clear any previous down state
            setIsAppDown(false);

            if (res.data && typeof res.data === 'object') {
                const { permissions: userPermissions = [],roles: userRoles=[], ...userData } = res.data;

                setUserProfile({
                    id: userData.username,
                    username: userData.username,
                    email: userData.email,
                    fullName: userData.fullName,
                });

                setPermissions(userPermissions || []);
                setRoles(userRoles||[]);

            } else {
                logger.warn("Unexpected API response format:", res.data);
                setUserProfile(null);
                setPermissions([]);
                setRoles([]);
            }

        } catch (err) {
            const status = err?.response?.status;

            if (!err.response) {
                // Network error — server is completely unreachable (no response at all)
                logger.error("App appears to be down (network error)", err);
                setIsAppDown(true);
            } else if (status >= 500) {
                // Server returned 5xx — treat as app down
                logger.error(`App appears to be down (HTTP ${status})`, err);
                setIsAppDown(true);
            } else {
                // 401, 403, 404, etc. — server is up, just an auth/data issue
                logger.error("Failed to fetch user profile", err);
                setIsAppDown(false);
            }

            setUserProfile(null);
            setPermissions([]);
            setRoles([]);
        } finally {
            setLoading(false);
            setHasCheckedOnce(true);
        }
    }, [syncAttempts]);

    const syncAndReload = useCallback(async () => {
        if (syncAttempts >= MAX_SYNC_ATTEMPTS) {
            logger.log(`Max sync attempts (${MAX_SYNC_ATTEMPTS}) reached. Stopping.`);
            return;
        }

        logger.log(`Sync attempt ${syncAttempts + 1}/${MAX_SYNC_ATTEMPTS}`);
        setProvisioning(true);
        setSyncAttempts(prev => prev + 1);

        try {
            await LS_API.post("/api/users/sync-permissions");
            await fetchUserProfile();
        } catch (error) {
            logger.error("Sync failed:", error);
        } finally {
            setProvisioning(false);
        }
    }, [fetchUserProfile, syncAttempts]);

    // Load user profile on mount
    useEffect(() => {
        fetchUserProfile();
    }, [fetchUserProfile]);

    // Helper functions
    const hasPermission = useCallback((permission) =>
        permissions.some(p =>
            (typeof p === 'string' ? p : p.name) === permission
        ), [permissions]);

    const hasAnyPermission = useCallback((...perms) =>
        perms.some(permission => hasPermission(permission)), [hasPermission]);

    const hasAllPermissions = useCallback((...perms) =>
        perms.every(permission => hasPermission(permission)), [hasPermission]);

    const hasRole = useCallback((rolePattern) => {
        if (!userProfile) return false;
        const patternRegex = new RegExp(`^${rolePattern.replace('*', '.*')}$`);
        return permissions.some(permission => patternRegex.test(permission));
    }, [userProfile, permissions]);

    const getUserInitials = useCallback(() => {
        if (!userProfile?.fullName) return userProfile?.username?.charAt(0).toUpperCase() || 'U';
        return userProfile.fullName
            .split(' ')
            .map(name => name.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 2);
    }, [userProfile]);

    const hasExhaustedRetries = syncAttempts >= MAX_SYNC_ATTEMPTS;

    return (
        <PermissionContext.Provider
            value={{
                permissions,
                roles,
                loading,
                provisioning,
                syncAndReload,
                hasPermission,
                hasAnyPermission,
                hasAllPermissions,
                reloadPermissions: fetchUserProfile,
                hasCheckedOnce,
                syncAttempts,
                maxSyncAttempts: MAX_SYNC_ATTEMPTS,
                hasExhaustedRetries,
                userProfile,
                isAuthenticated: !!userProfile,
                getUserInitials,
                hasRole,

                // ✅ NEW: expose app-down state so consumers can gate rendering
                isAppDown,

                // Convenience methods for common permission checks
                isAdmin: hasPermission(ADMIN_PERMISSION_EDIT) || hasPermission('admin.*'),
                canEditPermissions: hasPermission(ADMIN_PERMISSION_EDIT),
                canViewDocuments: hasPermission('ls.documents.view'),
                canViewPowerBI: (dashboard) => hasPermission(`powerbi.${dashboard}.view`),

                updateUserProfile: (updates) => {
                    setUserProfile(prev => ({
                        ...prev,
                        ...updates
                    }));
                }
            }}
        >
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissions = () => useContext(PermissionContext);